import { getAccessToken } from "../lib/auth";
import { parseApiError } from "../lib/api-error";

const API_BASE = "/api";

// ============================================================================
// Types
// ============================================================================

export interface HealthStatus {
  status: "Healthy" | "Progressing" | "Degraded" | "Suspended" | "Missing" | "Unknown";
  message?: string;
}

export interface SyncStatus {
  status: "Synced" | "OutOfSync" | "Unknown";
  revision?: string;
}

export interface AppSummary {
  name: string;
  namespace: string;
  project: string;
  health: HealthStatus;
  sync: SyncStatus;
  images: string[];
  wave: number;
  argocdUrl?: string;
}

export interface CategoryGroup {
  name: string;
  apps: AppSummary[];
}

export interface ClusterAppsResponse {
  summary: {
    total: number;
    healthy: number;
    degraded: number;
    outOfSync: number;
  };
  categories: CategoryGroup[];
  apps: AppSummary[];
}

export interface ResourceNode {
  group: string;
  version: string;
  kind: string;
  namespace: string;
  name: string;
  uid: string;
  parentRefs?: Array<{
    group: string;
    kind: string;
    namespace: string;
    name: string;
    uid: string;
  }>;
  health?: HealthStatus;
  info?: Array<{ name: string; value: string }>;
  createdAt?: string;
  images?: string[];
}

export interface ResourceStatus {
  group: string;
  version: string;
  kind: string;
  namespace: string;
  name: string;
  status: string;
  health?: HealthStatus;
}

export interface AppDetailResponse {
  name: string;
  namespace: string;
  project: string;
  health: HealthStatus;
  sync: SyncStatus;
  operationState?: {
    phase: string;
    message?: string;
    syncResult?: {
      revision: string;
    };
    startedAt?: string;
    finishedAt?: string;
  };
  reconciledAt?: string;
  images: string[];
  conditions?: Array<{
    type: string;
    message: string;
  }>;
  source?: {
    repoURL: string;
    path: string;
    targetRevision: string;
  };
  syncPolicy?: {
    automated?: {
      prune: boolean;
      selfHeal: boolean;
    };
  };
  resourceTree: ResourceNode[];
  resources?: ResourceStatus[];
  argocdUrl: string;
  grafanaUrl: string;
}

export interface PodInfo {
  name: string;
  namespace?: string;
  status: string;
  phase?: string;
  ready: boolean;
  restarts: number;
  age: string;
  containers?: Array<{
    name: string;
    image: string;
    ready: boolean;
    restartCount: number;
  }>;
}

export interface DeploymentStatus {
  name: string;
  namespace: string;
  replicas: number;
  readyReplicas: number;
  availableReplicas: number;
  updatedReplicas?: number;
}

export interface AppStatusResponse {
  app: {
    name: string;
    health: HealthStatus;
    sync: SyncStatus;
  };
  deployment: DeploymentStatus | null;
  pods: PodInfo[];
}

export interface RevisionHistory {
  id: number;
  revision: string;
  deployedAt: string;
  deployStartedAt?: string;
  source?: {
    repoURL: string;
    path: string;
    targetRevision: string;
  };
}

export interface PlatformPR {
  number: number;
  title: string;
  url: string;
  state: string;
  createdAt: string;
  author: string;
}

export interface ManifestResponse {
  path: string;
  content: string;
  sha: string;
}

export interface PRResponse {
  number: number;
  url: string;
}

// ============================================================================
// Fetch helper with auth
// ============================================================================

async function fetchAPI<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const token = await getAccessToken();

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options?.headers as Record<string, string>),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    throw await parseApiError(response);
  }

  return response.json();
}

// ============================================================================
// Log Types
// ============================================================================

export interface LogEntry {
  timestamp: string;
  level: string;
  logger: string;
  message: string;
  namespace: string;
  pod: string;
  container: string;
  node: string;
  correlation_id: string;
  context: Record<string, string>;
}

export interface LogSearchParams {
  namespace?: string;
  pod?: string;
  level?: string[];
  search?: string;
  correlationId?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export interface LogSearchResponse {
  entries: LogEntry[];
  total: number;
}

export interface LogFilters {
  namespaces: string[];
  levels: string[];
  pods: string[];
  loggers: string[];
}

export interface LogStats {
  buckets: Array<{
    time: string;
    level: string;
    count: number;
  }>;
}

// ============================================================================
// API
// ============================================================================

export const api = {
  cluster: {
    listApps: (env?: "prod" | "dev") =>
      fetchAPI<ClusterAppsResponse>(
        `/cluster/apps${env ? `?env=${env}` : ""}`
      ),
    getApp: (name: string) =>
      fetchAPI<AppDetailResponse>(`/cluster/apps/${name}`),
    getPods: (name: string) =>
      fetchAPI<{ pods: PodInfo[] }>(`/cluster/apps/${name}/pods`),
    getStatus: (name: string) =>
      fetchAPI<AppStatusResponse>(`/cluster/apps/${name}/status`),
    getEvents: (name: string) =>
      fetchAPI<{
        events: Array<{
          type: string;
          reason: string;
          message: string;
          count: number;
          lastTimestamp: string;
        }>;
      }>(`/cluster/apps/${name}/events`),
    getLogs: (name: string, pod?: string, tailLines = 100) => {
      const params = new URLSearchParams();
      if (pod) params.set("pod", pod);
      params.set("tailLines", String(tailLines));
      return fetchAPI<{ pod: string; logs: string[] }>(
        `/cluster/apps/${name}/logs?${params}`
      );
    },
    getHistory: (name: string) =>
      fetchAPI<{ history: RevisionHistory[] }>(
        `/cluster/apps/${name}/history`
      ),
    sync: (name: string, prune = false) =>
      fetchAPI<{ message: string }>(`/cluster/apps/${name}/sync`, {
        method: "POST",
        body: JSON.stringify({ prune }),
      }),
    restart: (name: string) =>
      fetchAPI<{ message: string }>(`/cluster/apps/${name}/restart`, {
        method: "POST",
      }),
    scale: (name: string, replicas: number) =>
      fetchAPI<{ message: string }>(`/cluster/apps/${name}/scale`, {
        method: "POST",
        body: JSON.stringify({ replicas }),
      }),
  },
  logs: {
    search: (params: LogSearchParams = {}) => {
      const searchParams = new URLSearchParams();
      if (params.namespace) searchParams.set("namespace", params.namespace);
      if (params.pod) searchParams.set("pod", params.pod);
      if (params.level?.length) params.level.forEach((l) => searchParams.append("level", l));
      if (params.search) searchParams.set("search", params.search);
      if (params.correlationId) searchParams.set("correlationId", params.correlationId);
      if (params.from) searchParams.set("from", params.from);
      if (params.to) searchParams.set("to", params.to);
      if (params.limit) searchParams.set("limit", String(params.limit));
      if (params.offset) searchParams.set("offset", String(params.offset));
      return fetchAPI<LogSearchResponse>(`/logs/search?${searchParams}`);
    },
    filters: (namespace?: string) =>
      fetchAPI<LogFilters>(`/logs/filters${namespace ? `?namespace=${namespace}` : ""}`),
    stats: (namespace?: string, from?: string, to?: string) => {
      const params = new URLSearchParams();
      if (namespace) params.set("namespace", namespace);
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      return fetchAPI<LogStats>(`/logs/stats?${params}`);
    },
    correlation: (id: string) =>
      fetchAPI<{ entries: LogEntry[] }>(`/logs/correlation/${id}`),
  },
  gitops: {
    listPRs: () => fetchAPI<{ prs: PlatformPR[] }>("/gitops/prs"),
    getManifest: (name: string) =>
      fetchAPI<ManifestResponse>(`/gitops/apps/${name}/manifest`),
    scale: (name: string, replicas: number) =>
      fetchAPI<{ pr: PRResponse }>(`/gitops/apps/${name}/scale`, {
        method: "POST",
        body: JSON.stringify({ replicas }),
      }),
    resources: (
      name: string,
      data: {
        requests?: { cpu?: string; memory?: string };
        limits?: { cpu?: string; memory?: string };
      }
    ) =>
      fetchAPI<{ pr: PRResponse }>(`/gitops/apps/${name}/resources`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
    env: (
      name: string,
      data: { set?: Record<string, string>; remove?: string[] }
    ) =>
      fetchAPI<{ pr: PRResponse }>(`/gitops/apps/${name}/env`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
  },
};
